import { atom } from "jotai";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DeductionRepository } from "@/infrastructure/repositories/deduction";
import {
  deductionAtom,
  remittanceItemIdAtom,
  updateDeductionDetailsAtom,
  deductionsAtom,
  updateDeductionIntelligenceAtom,
  DeductionEditMode,
} from "./deduction.atoms";
import { GetDeductionByRemittanceItemUseCase, UpdateDeductionIntelligenceUseCase, UpdateDeductionDetailsUseCase, SendForApprovalUseCase, GetDeductionByDeductionIdUseCase } from "@/core/usecases/deduction";
import { ApplicationError } from "@/infrastructure/errors/ApplicationError";
import { IUpdateDeductionDetailsRepositoryParams, IUpdateDeductionIntelligenceRepositoryParams } from "@/core/repositories/deduction";
import { currentPageAtom as currentRemittancePageAtom } from "../remittance";
import { currentPageAtom as currentRemittanceItemPageAtom } from "../remittanceItem";
import { groupByCheckAtom, refreshDeductionWorkspaceAtom } from "../main";
import { Deduction } from "@/core/entities/deduction";
import { createLogger } from "@/infrastructure/services/logger";
import { setHasAnyUnsavedChangesAtom } from "../drawer";

// GraphQL client
const client = GraphQlAdapter.getInstance().getClient();
// Deduction repository
const deductionRepository = new DeductionRepository(client);
const logger = createLogger("deduction.actions");
export const setDeductionsLoadingAtom = atom(null, async (get, set, isLoading: boolean) => {
  set(deductionAtom, { ...get(deductionAtom), isLoading });
});

// Update remittance item ID
export const setRemittanceItemIdAtom = atom(null, async (get, set, remittanceItemId: number) => {
  set(deductionAtom, { ...get(deductionAtom), remittanceItemId });
});

// Action atoms
export const getDeductionsAtom = atom(null, async (get, set, refetch: boolean = false) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    isLoading: true,
  });
  try {
    const remittanceItemId = get(remittanceItemIdAtom);
    if (!remittanceItemId) {
      throw new ApplicationError("Remittance item ID is not found");
    }
    const deductions = await new GetDeductionByRemittanceItemUseCase(deductionRepository).execute({ remittance_item_id: remittanceItemId }, refetch);

    set(deductionAtom, {
      ...get(deductionAtom),
      deductions: deductions.deductions,
      count: deductions.count,
      isLoading: false,
      error: null,
    });
  } catch (error: unknown) {
    set(deductionAtom, {
      ...get(deductionAtom),
      remittanceItemId: null,
      deductions: [],
      count: 0,
      isLoading: false,
      error: error as Error,
    });
  }
});

export const getDeductionByDeductionIdAtom = atom(null, async (get, set, deductionId: number) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    isLoading: true,
  });
  try {
    const deduction = await new GetDeductionByDeductionIdUseCase(deductionRepository).execute({ deduction_id: deductionId });
    const newDeduction = new Deduction(deduction.deduction);

    const deductions = get(deductionsAtom);
    const newDeductions = deductions.map(deduction => (deduction.id === newDeduction.id ? newDeduction : deduction));

    set(deductionAtom, {
      ...get(deductionAtom),
      deductions: newDeductions,
      isLoading: false,
      error: null,
    });
  } catch (error: unknown) {
    set(deductionAtom, {
      ...get(deductionAtom),
      deductions: [],
      isLoading: false,
      error: error as Error,
    });
  }
});

export const setDeductionDetailsAtom = atom(null, async (get, set, deductionDetails: IUpdateDeductionDetailsRepositoryParams) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    updateDeductionDetails: deductionDetails,
  });
});

export const clearDeductionDetailsAtom = atom(null, async (get, set) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    updateDeductionDetails: null,
    updateError: null,
  });
});

export const setEditModeAtom = atom(null, async (get, set, editMode: DeductionEditMode | null) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    editMode,
  });
});

export const patchDeductionDetailsAtom = atom(null, async (get, set) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    isDetailsUpdating: true,
  });

  try {
    const updateDeductionDetailsRequest = get(updateDeductionDetailsAtom);

    if (!updateDeductionDetailsRequest) {
      throw new ApplicationError("Update deduction details request is not found");
    }

    const deductions = get(deductionsAtom);
    const groupByCheck = get(groupByCheckAtom);
    const currentRemittancePage = get(currentRemittancePageAtom);
    const currentRemittanceItemPage = get(currentRemittanceItemPageAtom);
    const currentPage = groupByCheck ? currentRemittancePage : currentRemittanceItemPage;

    const { updateDeductionDetails } = await new UpdateDeductionDetailsUseCase(deductionRepository).execute(updateDeductionDetailsRequest, currentPage);

    set(deductionAtom, {
      ...get(deductionAtom),
      isDetailsUpdating: false,
      editMode: null,
      deductions: deductions.map(deduction => (deduction.id === updateDeductionDetails.id ? updateDeductionDetails : deduction)),
    });

    set(setHasAnyUnsavedChangesAtom, false);

    await set(refreshDeductionWorkspaceAtom, true);

    return updateDeductionDetails;
  } catch (error: unknown) {
    logger.error("patchDeductionDetailsAtom error", error);
    set(deductionAtom, {
      ...get(deductionAtom),
      isDetailsUpdating: false,
      updateError: error as Error,
    });
  }
});

export const setDeductionIntelligenceAtom = atom(null, async (get, set, deductionIntelligence: IUpdateDeductionIntelligenceRepositoryParams) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    updateDeductionIntelligence: deductionIntelligence,
  });
});

export const clearDeductionIntelligenceAtom = atom(null, async (get, set) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    updateDeductionIntelligence: null,
    updateError: null,
  });
});

export const patchDeductionIntelligenceAtom = atom(null, async (get, set) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    isIntelligenceUpdating: true,
  });

  try {
    const updateDeductionIntelligenceRequest = get(updateDeductionIntelligenceAtom);
    const groupByCheck = get(groupByCheckAtom);
    const deductions = get(deductionsAtom);
    const currentRemittancePage = get(currentRemittancePageAtom);
    const currentRemittanceItemPage = get(currentRemittanceItemPageAtom);
    const currentPage = groupByCheck ? currentRemittancePage : currentRemittanceItemPage;

    if (!updateDeductionIntelligenceRequest) {
      throw new ApplicationError("Update deduction intelligence request is not found");
    }

    const { updateDeductionIntelligence } = await new UpdateDeductionIntelligenceUseCase(deductionRepository).execute(
      updateDeductionIntelligenceRequest,
      currentPage
    );

    set(deductionAtom, {
      ...get(deductionAtom),
      isIntelligenceUpdating: false,
      editMode: null,
      deductions: deductions.map(deduction => (deduction.id === updateDeductionIntelligence.id ? updateDeductionIntelligence : deduction)),
    });
    set(setHasAnyUnsavedChangesAtom, false);

    await set(refreshDeductionWorkspaceAtom, true);

    return updateDeductionIntelligence;
  } catch (error: unknown) {
    set(deductionAtom, {
      ...get(deductionAtom),
      isIntelligenceUpdating: false,
      updateError: error as Error,
    });
  }
});

export const sendForApprovalAtom = atom(null, async (get, set, deduction: Deduction, comment: string) => {
  set(deductionAtom, {
    ...get(deductionAtom),
    isApprovalSending: true,
  });

  try {
    const deductions = get(deductionsAtom);
    const groupByCheck = get(groupByCheckAtom);
    const currentRemittancePage = get(currentRemittancePageAtom);
    const currentRemittanceItemPage = get(currentRemittanceItemPageAtom);
    const currentPage = groupByCheck ? currentRemittancePage : currentRemittanceItemPage;

    const { sendForApproval } = await new SendForApprovalUseCase(deductionRepository).single({
      deduction_id: deduction.id,
      remittance_item_id: deduction.remittanceItemId,
      comment: comment,
      action: "APPROVE",
    }, currentPage);

    // Keep isApprovalSending true until refresh finishes so the modal Submit
    // button stays disabled for the full submit → refresh → close window.
    set(deductionAtom, {
      ...get(deductionAtom),
      deductions: deductions.map(eachDeduction => (eachDeduction.id === sendForApproval.id ? sendForApproval : eachDeduction)),
    });

    await set(refreshDeductionWorkspaceAtom, true);

    set(deductionAtom, {
      ...get(deductionAtom),
      isApprovalSending: false,
    });

    return sendForApproval;
  } catch (error: unknown) {
    logger.error("sendForApprovalAtom error", error);
    set(deductionAtom, {
      ...get(deductionAtom),
      isApprovalSending: false,
    });
  }
});
